import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Flex, Text } from "rebass/styled-components";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import { SiEpicgames } from "react-icons/si";

import { analytics } from "MVVM/Analytics";
import { ESupportedGames, IClip } from "MVVM/@types";
import { MatchHistoryMatchThumbnailState } from "MVVM/State/matchHistory";
import { URLS } from "MVVM/Utilities/Maps";
import { Button } from "MVVM/Components/Button";
import { TMoment } from "MVVM/Views/MatchHistory/@types";
import { MomentDetails } from "../MomentDetails";
import {
  StyledSteamIcon,
  StyledRiotIcon,
} from "MVVM/Views/MatchHistory/styledComponents";
import { withReturn } from "MVVM/Utilities/Auth/loginRedirect";

export const DownloadLoginModal = ({
  game,
  moment,
  clip,
  matchTitle,
}: {
  game: ESupportedGames;
  moment?: TMoment;
  clip?: IClip;
  matchTitle?: string;
}) => {
  const history = useHistory();
  const matchThumbnail = useRecoilValue(MatchHistoryMatchThumbnailState);
  const { t } = useTranslation();

  const thumbnailUrl = clip?.thumbnailStandardUrl || matchThumbnail;

  return (
    <Container>
      <ClipBanner
        style={{
          backgroundImage: `linear-gradient(to bottom, transparent, var(--Gray-750, #1c2a34)), url(${thumbnailUrl})`,
        }}
      />
      {!!moment && <MomentDetails moment={moment} />}
      {!!matchTitle && !moment && (
        <StyledTitleText variant="text.large3" fontWeight={700}>
          {matchTitle}
        </StyledTitleText>
      )}
      <Flex
        style={{ padding: "12px 24px 24px", gap: "24px" }}
        flexDirection="column"
      >
        <Flex flexDirection="column" textAlign="center" style={{ gap: "12px" }}>
          <Title color="ice" variant="text.titleSmall">
            Login to Download this Clip!
          </Title>
          <Subtitle color="chalk" variant="text.paragraphMedium">
            Create a FREE Allstar.gg account to download this clip, and create,
            edit & share moments from any match!
          </Subtitle>
        </Flex>
        <DownloadSignupOptions game={game} />
        <Flex style={{ gap: 10 }} justifyContent="center">
          <Text color="chalk" variant={"text.labelMedium"}>
            {t("downloadLoginModal.signupText", {
              defaultValue: "Don't want to login with this platform?",
            })}
          </Text>
          <LoginText
            variant={"text.labelMedium"}
            color="envy"
            onClick={() => {
              analytics.track("Download Login Modal", {
                type: "signup",
                action: "click",
                source: "iframe_download",
              });
              history.push(withReturn(URLS.SIGNUP));
            }}
          >
            {t("signup.signup", "Sign up")}
          </LoginText>
        </Flex>
      </Flex>
    </Container>
  );
};

const DownloadSignupOptions = ({ game }: { game: ESupportedGames }) => {
  return (
    <>
      {game === ESupportedGames.CS2 && <SteamDownloadSignup />}
      {game === ESupportedGames.LOL && <RiotDownloadSignup />}
      {game === ESupportedGames.FORTNITE && <EpicDownloadSignup />}
    </>
  );
};

const SteamDownloadSignup = () => {
  const history = useHistory();
  return (
    <SteamButton
      size="small"
      onClick={() => {
        analytics.track("Download Login Modal", {
          type: "Steam",
          action: "click",
          source: "iframe_download",
        });
        history.push(withReturn(`${URLS.AUTH_ROUTES.STEAM}?flow=signup`));
      }}
    >
      <Flex justifyContent="center" alignItems="center">
        <StyledSteamIcon />
        <Text>Download with Steam</Text>
      </Flex>
    </SteamButton>
  );
};

const RiotDownloadSignup = () => {
  const history = useHistory();
  return (
    <RiotButton
      size="small"
      onClick={() => {
        analytics.track("Download Login Modal", {
          type: "Riot",
          action: "click",
          source: "iframe_download",
        });
        history.push(withReturn(`${URLS.AUTH_ROUTES.RIOT}?flow=signup`));
      }}
    >
      <Flex justifyContent="center" alignItems="center">
        <StyledRiotIcon />
        <Text>Download with Riot</Text>
      </Flex>
    </RiotButton>
  );
};

const EpicDownloadSignup = () => {
  const history = useHistory();
  return (
    <EpicButton
      size="small"
      onClick={() => {
        analytics.track("Download Login Modal", {
          type: "Epic",
          action: "click",
          source: "iframe_download",
        });
        history.push(withReturn(`${URLS.AUTH_ROUTES.EPIC}?flow=signup`));
      }}
    >
      <Flex justifyContent="center" alignItems="center">
        <StyledEpicIcon />
        <Text>Download with Epic</Text>
      </Flex>
    </EpicButton>
  );
};

const Container = styled(Flex)`
  position: relative;
  width: 448px;
  flex-direction: column;
  background-color: var(--Gray-750, #1c2a34);
  text-align: center;
  border-radius: 14px;
  border: ${({ theme }) => `1px solid ${theme.colors.envyAlpha20}`};
  box-shadow: ${({ theme }) => `0px 0px 16px 0px ${theme.colors.envyAlpha20}`};

  @media (max-width: 500px) {
    width: 390px;
  }

  @media (max-width: 425px) {
    width: 100%;
  }
`;

const ClipBanner = styled(Flex)`
  background-size: cover;
  background-position: center;
  height: 192px;
  width: 100%;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
`;

const LoginText = styled(Text)`
  cursor: pointer;
  margin-right: 15px;
  min-width: 55px;

  @media (max-width: 425px) {
    margin-right: 0px;
  }
`;

const Title = styled(Text)`
  @media (max-width: 425px) {
    font-size: 24px;
    line-height: 28px;
  }
`;

const Subtitle = styled(Text)`
  @media (max-width: 425px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const StyledTitleText = styled(Text)`
  position: absolute;
  top: 20%;
  left: 0;
  right: 0;
  text-align: center;
`;

const LinkButton = styled(Button)`
  border-radius: 8px;
  color: var(--Ice-100, var(--colors-ice-500100, #f7fcfd));
  min-width: 160px;
  margin-top: 0px;
`;

const SteamButton = styled(LinkButton)`
  background-color: rgba(18, 120, 161, 0.2);
  transition: background-color 0.1s ease;
  border: 1px solid rgba(18, 120, 161, 1);
  &:hover {
    background-color: rgba(18, 120, 161, 0.9);
  }
`;

const RiotButton = styled(LinkButton)`
  background-color: rgba(241, 43, 21, 0.2);
  transition: background-color 0.1s ease;
  border: 1px solid rgba(241, 43, 21, 1);
  &:hover {
    background-color: rgba(241, 43, 21, 0.9);
  }
`;

const StyledEpicIcon = styled(SiEpicgames)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-right: 8px;
`;

const EpicButton = styled(LinkButton)`
  background-color: rgba(31, 31, 31, 0.2);
  transition: background-color 0.1s ease;
  border: 1px solid ${(props) => props.theme.colors.iceAlpha60};
  &:hover {
    background-color: rgba(31, 31, 31, 0.9);
  }
`;
